import { faSearchMinus, faSearchPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import { InfoBarContext } from "../../../../common/info";
import { publish, subscribe, unsubscribe } from "../../../../eventBus";
import PdfFormErrorIndicator from "./PdfFormErrorIndicator";
import PdfFormErrors from "./PdfFormErrors";
import { useTranslation } from "react-i18next";
import {
  isDesktop,
  isWideScreen,
  isFullHD,
  is2K,
  is1080p,
  isMobile,
  isTablet,
  isMobileSmall,
} from "../../../../utils/screenUtil";
export const selectAllInputsQuery = (root) =>
  root.querySelectorAll(".react-pdf__Page__annotations.annotationLayer > section > input,textarea");

function PdfForm({ pdf, pdfFormStateObjectRef, dispatch }) {
  const [numPages, setNumPages] = useState(null);
  const documentRef = useRef(null);
  const infoBar = useContext(InfoBarContext);
  const { t } = useTranslation();

  const [scale, setScale] = useState();

  const [width, setWidth] = useState({ innerWidth: window.innerWidth });

  const [zoomCounter, setZoomCounter] = useState(0);

  useEffect(() => {
    function handleResize() {
      setWidth({ innerWidth: window.innerWidth });
    }
    window.addEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (is2K()) {
      setScale(1.8);
    } else if (is1080p()) {
      setScale(1.6);
    } else if (isFullHD()) {
      setScale(1.5);
    } else if (isWideScreen()) {
      setScale(1.4);
    } else if (isDesktop()) {
      setScale(1.2);
    } else if (isTablet()) {
      setScale(1);
    } else if (isMobile()) {
      setScale(0.6);
    } else if (isMobileSmall()) {
      setScale(0.4);
    }
  }, [width]);

  const zoomIn = () => {
    setZoomCounter(0);
    setZoomCounter(zoomCounter + 1);
    if (zoomCounter < 3) {
      setScale(scale + 0.2);
    } else if (zoomCounter === 3) {
      setZoomCounter(3);
    }
  };

  const zoomOut = () => {
    setZoomCounter(0);
    setZoomCounter(zoomCounter - 1);
    if (zoomCounter > -3) {
      setScale(scale - 0.2);
    } else if (zoomCounter === -3) {
      setZoomCounter(-3);
    }
  };

  const getInputElementValue = (inputField) => {
    let value;
    if (inputField.type === "text" || inputField.type === "textarea") {
      value = inputField.value;
    } else if (inputField.type === "checkbox" || inputField.type === "radio") {
      value = inputField.checked;
    }
    return value.toString();
  };

  const getInputElements = useCallback(
    () => selectAllInputsQuery(documentRef.current),
    [documentRef]
  );

  const getField = useCallback(
    (inputField) => {
      let id = inputField.parentElement.dataset.annotationId;
      let annotation = pdfFormStateObjectRef.annotationLayer.annotations[id];
      let fieldDefinition = pdfFormStateObjectRef.fieldDefinitions[annotation.name];
      let value = getInputElementValue(inputField);

      return { id, annotation, fieldDefinition, value };
    },
    [pdfFormStateObjectRef.annotationLayer.annotations, pdfFormStateObjectRef.fieldDefinitions]
  );

  const setInputElementStyle = useCallback((inputField, valid) => {
    let classes = inputField.classList;
    let clazz = "has-border-red-1-important";

    valid ? classes.remove(clazz) : classes.add(clazz);
  }, []);

  const validateInputElementValue = useCallback((name, type, regex, required, value) => {
    if (!regex && !required) {
      return true;
    }

    if (required && !regex && type === "Btn") {
      const radios = Array.prototype.slice.call(
        document.querySelectorAll(`input[type=radio][name="${name}"]`)
      );

      if (radios !== []) {
        const questions = Object.values(
          radios.reduce(
            (result, el) =>
              Object.assign(result, { [el.name]: (result[el.name] || []).concat(el) }),
            {}
          )
        );

        const hasUnanswered = questions.some((question) => !question.some((el) => el.checked));

        const isChecked = questions.some(function (question) {
          if (!question.some((el) => el.value === "true" && el.checked)) {
            radios.forEach((radio) => {
              setInputElementStyle(radio, true);
            });
            return true;
          }
        });

        if (hasUnanswered && isChecked) {
          return false;
        } else {
          return true;
        }
      } else return;
    }

    if (type === "Tx") {
      if (!regex && required) {
        if (value.length > 0) {
          return true;
        } else return false;
      }

      let validByRegex = new RegExp(regex).test(value);
      return required
        ? required && value.length > 0 && validByRegex
        : value.length > 0
        ? validByRegex
        : true; // If not required and value is not entered ignore
    } else {
      return required === value;
    }
  }, []);

  const validateForm = useCallback(() => {
    let validationErrors = [];

    getInputElements().forEach((field) => {
      let id = field.parentElement.dataset.annotationId;
      let annotation = pdfFormStateObjectRef.annotationLayer.annotations[id];
      let fieldDefinition = pdfFormStateObjectRef.fieldDefinitions[annotation.name];

      if (fieldDefinition) {
        let value = getInputElementValue(field);
        let valid = validateInputElementValue(
          annotation.name,
          annotation.type,
          fieldDefinition.regex,
          fieldDefinition.required,
          value
        );
        if (!valid) {
          validationErrors.push({
            valid,
            id,
            fieldDefinition,
            name: annotation.name,
            value,
            exportValue: annotation.exportValue,
          });
          setInputElementStyle(field, valid);
        }
      }
    });

    return validationErrors;
  }, [
    getInputElements,
    pdfFormStateObjectRef.annotationLayer.annotations,
    validateInputElementValue,
    setInputElementStyle,
    pdfFormStateObjectRef.fieldDefinitions,
  ]);

  const onInputListener = useCallback(
    (e) => {
      const {
        id,
        annotation: { name, exportValue, type, checkBox },
        value,
        fieldDefinition,
      } = getField(e.currentTarget);

      if (fieldDefinition) {
        let valid = validateInputElementValue(
          name,
          type,
          fieldDefinition.regex,
          fieldDefinition.required,
          value
        );

        let fieldValidation = {
          valid,
          id,
          fieldDefinition,
          name,
          value,
          exportValue,
          type,
          checkBox,
        };

        dispatch({ type: "pdf-field-validation", payload: fieldValidation });
        setInputElementStyle(e.currentTarget, valid);
      }
    },
    [dispatch, getField, setInputElementStyle, validateInputElementValue]
  );

  const setInputElementValue = useCallback(
    (field, annotation, current) => {
      let value =
        current && current.value !== undefined
          ? current.value
          : pdfFormStateObjectRef.fieldDefinitions[annotation.name]
          ? pdfFormStateObjectRef.fieldDefinitions[annotation.name].defaultValue
          : null;

      if (annotation.type === "Tx") {
        field.value = value;
      } else if (annotation.type === "Btn") {
        if (annotation.radioButton) {
          if (current && current.id && field.parentElement.dataset.annotationId === current.id) {
            if (value) {
              field.checked = value;
            }
          }
        } else {
          field.checked = value;
        }
      }
    },
    [pdfFormStateObjectRef.fieldDefinitions]
  );

  const setInputElements = useCallback(
    (annotations) => {
      getInputElements().forEach((field) => {
        let id = field.parentElement.dataset.annotationId;
        let annotation = annotations[id];
        field.addEventListener("focus", (e) => {
          if (id === e.srcElement.id) {
            if (isMobile()) {
              document.getElementById(e.srcElement.id).style =
                "transform: scale(1.1, 1.6) translate(1%,0); background-color: rgba(252,	238,	173, 1); z-index: 1000; zoom: 1.5;";
            } else if (isMobileSmall()) {
              document.getElementById(e.srcElement.id).style =
                "transform: scale(1.1, 1.6) translate(1%,0); background-color: rgba(252,	238,	173, 1); z-index: 1000; zoom: 1.5;";
            }
          }
        });
        field.addEventListener("blur", (e) => {
          if (id === e.srcElement.id) {
            if (isMobile()) {
              document.getElementById(e.srcElement.id).style =
                "transform: scale(1, 1) translate(0,0); background-color: white; z-index: -1000; zoom: 1;";
            } else if (isMobileSmall()) {
              document.getElementById(e.srcElement.id).style =
                "transform: scale(1, 1) translate(0,0); background-color: white; z-index: -1000; zoom: 1;";
            }
          }
        });
        if (annotation) {
          // Attach onInput listener
          field.oninput = onInputListener;

          // Set to value if such existed
          let currentFieldValue = pdfFormStateObjectRef.currentFieldValues[annotation.name];

          setInputElementValue(field, annotation, currentFieldValue);

          // Add styling based on input validity
          if (pdfFormStateObjectRef.validationErrors[annotation.name]) {
            setInputElementStyle(
              field,
              pdfFormStateObjectRef.validationErrors[annotation.name].valid
            );
          }
        }
      });
    },
    [
      onInputListener,
      setInputElementValue,
      pdfFormStateObjectRef.currentFieldValues,
      setInputElementStyle,
      getInputElements,
      pdfFormStateObjectRef.validationErrors,
    ]
  );

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const pdfDocument = useMemo(() => {
    return (
      <Document
        file={pdf}
        onLoadSuccess={onDocumentLoadSuccess}
        className="document overflow-y-scroll container pb-4 scrollbar-no-width"
      >
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            scale={scale}
            //renderInteractiveForms={true} // possibly deprecated
            renderForms={true} // replaced with
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            className="pdf-border"
            onGetAnnotationsSuccess={(annotations) => {
              dispatch({
                type: "annotations",
                payload: {
                  pageIndex: index,
                  annotations: annotations,
                  // annotationsLoaded should always be true here because it is called onGetAnnotationsSuccess
                  annotationsLoaded: true, // changed from numPages - 1 === index
                },
              });
            }}
          />
        ))}
      </Document>
    );
  }, [pdf, numPages, scale, dispatch]);

  useEffect(() => {
    if (pdfFormStateObjectRef.annotationLayer.loaded) {
      setInputElements(pdfFormStateObjectRef.annotationLayer.annotations);
    }
  }, [
    pdfFormStateObjectRef.annotationLayer.loaded,
    pdfFormStateObjectRef.annotationLayer.annotations,
    setInputElements,
  ]);

  useEffect(() => {
    publish({
      type: "pdf-form-errors",
      payload: pdfFormStateObjectRef.validationErrors,
    });
  }, [pdfFormStateObjectRef.validationErrors]);

  useEffect(() => {
    dispatch({ type: "default-values", payload: pdfFormStateObjectRef.fieldDefinitions });
  }, [dispatch, pdfFormStateObjectRef.fieldDefinitions]);

  useEffect(() => {
    let subscriber = subscribe("validate-pdf-form", () => {
      let validationErrors = validateForm();
      if (validationErrors.length > 0) {
        dispatch({ type: "pdf-form-validation", payload: validationErrors });
      }
      return validationErrors;
    });

    return () => unsubscribe(subscriber);
  }, [validateForm, dispatch]);

  return (
    <div className="pdf section columns is-centered">
      <div ref={documentRef}>
        <div className="has-text-centered">
          <FontAwesomeIcon icon={faSearchPlus} className="is-clickable" onClick={zoomIn} />
          <FontAwesomeIcon icon={faSearchMinus} className="is-clickable" onClick={zoomOut} />
          <PdfFormErrorIndicator
            on={Object.keys(pdfFormStateObjectRef.validationErrors).length > 0}
            onClick={() => {
              infoBar.toggle(
                t("requests.submission.pdf.pdfFormErrorInfo.errorMessageTitle"),
                () => (
                  <PdfFormErrors errors={JSON.stringify(pdfFormStateObjectRef.validationErrors)} />
                )
              );
            }}
          />
        </div>
        {pdfDocument}
      </div>
    </div>
  );
}

export default PdfForm;
